import { useState, useEffect, useRef } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CalendarClock, Plus, Play, Trash2, Power, ImagePlus, Loader2, Download, Film, Clapperboard, AlertTriangle, Megaphone, Quote, Lightbulb, Sparkles, Rocket, Share2, Send, CheckCircle2, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";

const CADENCE = [{ id: "hourly", label: "Toutes les heures" }, { id: "6h", label: "Toutes les 6 h" }, { id: "daily", label: "1× / jour" }];

const TEMPLATES = [
  { id: "promo", icon: Megaphone, name: "Promo flash", cadence: "daily", idea: "Clip vertical 9:16 énergique pour une PROMO flash : offre du jour, texte gros et lisible, ambiance premium or et noir, rythme rapide, appel à l'action « Profitez-en maintenant »." },
  { id: "citation", icon: Quote, name: "Citation inspirante", cadence: "daily", idea: "Clip vertical 9:16 minimaliste avec une CITATION inspirante du jour, typographie élégante, fond cinématographique doux, ambiance motivation, musique calme." },
  { id: "teaser", icon: Sparkles, name: "Teaser produit", cadence: "6h", idea: "Clip vertical 9:16 TEASER PRODUIT : gros plans esthétiques, lumière studio, révélation du produit, texte « Bientôt disponible », style publicité haut de gamme." },
  { id: "astuce", icon: Lightbulb, name: "Astuce du jour", cadence: "daily", idea: "Clip vertical 9:16 ASTUCE rapide : un conseil concret expliqué en quelques secondes, sous-titres dynamiques, ton amical et direct, format tips." },
  { id: "coulisses", icon: Clapperboard, name: "Coulisses", cadence: "daily", idea: "Clip vertical 9:16 COULISSES (behind the scenes) : ambiance authentique, plans variés, storytelling court sur les coulisses de la marque." },
];

export default function AutoStudio() {
  const { refreshCredits } = useOutletContext();
  const [unlimited, setUnlimited] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [videos, setVideos] = useState([]);
  const [social, setSocial] = useState({ configured: false, accounts: [] });
  const [publishing, setPublishing] = useState(null);
  const [form, setForm] = useState({ name: "Ma série", idea: "", cadence: "daily", time_of_day: "09:00", daily_limit: 3, image: null, autopost: false, platforms: [], caption: "" });
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/schedules").then((r) => { setSchedules(r.data.schedules); setUnlimited(r.data.unlimited); }).catch(() => {});
    api.get("/videos").then((r) => setVideos(r.data)).catch(() => {});
    api.get("/social/status").then((r) => setSocial(r.data)).catch(() => {});
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const onImage = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1_400_000) { toast.error("Image trop lourde (max ~1,4 Mo)"); return; }
    const rd = new FileReader();
    rd.onload = () => setForm((s) => ({ ...s, image: rd.result }));
    rd.readAsDataURL(f);
  };

  const create = async () => {
    if (!form.idea.trim()) { toast.error("Décrivez une idée"); return; }
    setCreating(true);
    try {
      await api.post("/schedules", form);
      toast.success("Série programmée — génération automatique 24/7");
      setForm({ name: "Ma série", idea: "", cadence: "daily", time_of_day: "09:00", daily_limit: 3, image: null, autopost: false, platforms: [], caption: "" });
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de créer la série"); }
    setCreating(false);
  };

  const applyTemplate = (t) => {
    setForm((s) => ({ ...s, name: t.name, idea: t.idea, cadence: t.cadence, time_of_day: "09:00" }));
    toast.success(`Modèle « ${t.name} » chargé — ajustez puis programmez`);
    document.querySelector('[data-testid="schedule-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const quickLaunch = async (t) => {
    try {
      await api.post("/schedules", { name: t.name, idea: t.idea, cadence: t.cadence, time_of_day: "09:00", daily_limit: form.daily_limit, active: true });
      toast.success(`Série « ${t.name} » lancée — génération automatique 24/7`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Impossible de lancer la série"); }
  };

  const toggle = async (s) => { await api.put(`/schedules/${s.id}`, { ...s, active: !s.active }); load(); };
  const del = async (id) => { await api.delete(`/schedules/${id}`); load(); };
  const runNow = async (id) => { await api.post(`/schedules/${id}/run-now`); toast.success("Génération lancée… (apparaît dans la bibliothèque)"); setTimeout(load, 4000); refreshCredits?.(); };
  const delVideo = async (id) => { await api.delete(`/videos/${id}`); load(); };

  const nextLabel = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };

  const PLAT_LABELS = { tiktok: "TikTok", instagram: "Instagram", facebook: "Facebook", youtube: "YouTube", linkedin: "LinkedIn", gmb: "Google Business", twitter: "X", pinterest: "Pinterest", telegram: "Telegram", bluesky: "Bluesky", reddit: "Reddit", threads: "Threads", snapchat: "Snapchat" };
  const availablePlatforms = (social.accounts && social.accounts.length ? social.accounts : ["tiktok", "instagram"]);
  const togglePlatform = (id) => setForm((s) => ({ ...s, platforms: s.platforms.includes(id) ? s.platforms.filter((p) => p !== id) : [...s.platforms, id] }));

  const publish = async (v) => {
    const plats = social.accounts?.length ? social.accounts : [];
    if (!plats.length) { toast.error("Aucun réseau connecté — connectez vos réseaux sur Ayrshare"); return; }
    setPublishing(v.id);
    try {
      await api.post(`/videos/${v.id}/publish`, { platforms: plats, caption: v.idea });
      toast.success(`Publication lancée sur ${plats.join(", ")}… le statut s'actualise`);
      setTimeout(load, 3000);
    } catch (e) { toast.error(e.response?.data?.detail || "Publication refusée"); }
    setPublishing(null);
  };

  const postErrorText = (err) => {
    if (!err) return "publication échouée";
    if (Array.isArray(err)) return err.map((x) => x.message || x).join(" · ");
    return typeof err === "string" ? err : "publication échouée";
  };

  const FLYERS = [
    "https://static.prod-images.emergentagent.com/jobs/42dc9706-d4e4-4474-a15b-bda4ac18e429/images/12b45595c398db9f172c515c85497587319ff848332e4b6aeb57f3a7970c6384.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/42dc9706-d4e4-4474-a15b-bda4ac18e429/images/9e66ced82c6ccc810c753d02d894970a6a4e3cfb27ca17fe7b44d7282f01e6b8.jpeg",
    "https://static.prod-images.emergentagent.com/jobs/42dc9706-d4e4-4474-a15b-bda4ac18e429/images/67ad5db2b621121aeb2f67fac6f22dc894cca9997c1e53d073f00aaf54bde325.jpeg",
  ];
  const [imgTest, setImgTest] = useState({ image: FLYERS[0], platforms: [], busy: false, result: null });
  const imgPlatforms = availablePlatforms.filter((p) => !["tiktok", "youtube"].includes(p));
  const toggleImgPlat = (id) => setImgTest((s) => ({ ...s, platforms: s.platforms.includes(id) ? s.platforms.filter((p) => p !== id) : [...s.platforms, id] }));

  const publishImage = async () => {
    if (!imgTest.platforms.length) { toast.error("Choisissez au moins un réseau"); return; }
    setImgTest((s) => ({ ...s, busy: true, result: null }));
    try {
      const { data } = await api.post("/social/post-image", { image_url: imgTest.image, caption: "CRÉRÉA — Une clé, tous les modèles. #IA #CRÉRÉA", platforms: imgTest.platforms });
      if (data.ok) { toast.success("Image publiée sur vos réseaux !"); setImgTest((s) => ({ ...s, result: { ok: true, ids: data.postIds } })); }
      else { toast.error("Publication refusée"); setImgTest((s) => ({ ...s, result: { ok: false, error: data.error } })); }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur de publication"); }
    setImgTest((s) => ({ ...s, busy: false }));
  };

  return (
    <div className="space-y-6" data-testid="auto-studio-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold-400">CRÉRÉA · Auto-Studio</p>
        <h1 className="mt-1 flex items-center gap-2 font-display text-3xl font-bold tracking-tight sm:text-4xl"><CalendarClock className="h-7 w-7 text-gold-400" />Vidéos courtes en pilote automatique</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Donnez une idée (et une image de référence), choisissez une cadence — le site génère vos clips verticaux <span className="text-foreground">24h/24</span> et les range dans votre bibliothèque, prêts à publier sur TikTok/Reels.</p>
      </div>

      {/* Templates */}
      <div data-testid="templates-section">
        <div className="mb-3 flex items-center gap-2"><Rocket className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Modèles de séries — lancez en 1 clic</h3></div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {TEMPLATES.map((t) => (
            <div key={t.id} className="group flex flex-col rounded-2xl border border-border bg-card p-4 transition-colors hover:border-gold-500/50" data-testid={`template-${t.id}`}>
              <div className="mb-2 grid h-9 w-9 place-items-center rounded-lg bg-gold-500/15 text-gold-400"><t.icon className="h-4 w-4" /></div>
              <p className="font-display text-sm font-semibold">{t.name}</p>
              <p className="mt-1 line-clamp-3 flex-1 text-xs leading-relaxed text-muted-foreground">{t.idea}</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => applyTemplate(t)} data-testid={`template-use-${t.id}`} className="flex-1 rounded-full border border-border px-2 py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground">Utiliser</button>
                <button onClick={() => quickLaunch(t)} data-testid={`template-launch-${t.id}`} className="flex items-center gap-1 rounded-full bg-gold-500 px-3 py-1.5 text-xs font-medium text-black transition-colors hover:bg-gold-400"><Play className="h-3 w-3" />Lancer</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Free image test */}
      <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5" data-testid="image-test-section">
        <div className="mb-1 flex items-center gap-2"><Send className="h-4 w-4 text-emerald-400" /><h3 className="font-display font-semibold">Test gratuit — publier une image</h3></div>
        <p className="mb-3 text-xs text-muted-foreground">Le post vidéo nécessite un plan Ayrshare payant. Les <span className="text-foreground">images</span> passent en plan gratuit : validez la diffusion vers vos vrais réseaux dès maintenant.</p>
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex gap-2">
            {FLYERS.map((f, i) => (
              <button key={i} onClick={() => setImgTest((s) => ({ ...s, image: f }))} data-testid={`img-test-pick-${i}`}
                className={`overflow-hidden rounded-lg border-2 transition-colors ${imgTest.image === f ? "border-gold-500" : "border-transparent hover:border-border"}`}>
                <img src={f} alt="" className="h-24 w-16 object-cover" />
              </button>
            ))}
          </div>
          <div className="min-w-[220px] flex-1">
            <div className="mb-2 flex flex-wrap gap-2">
              {imgPlatforms.map((id) => {
                const on = imgTest.platforms.includes(id);
                return (
                  <button key={id} onClick={() => toggleImgPlat(id)} data-testid={`img-platform-${id}`}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-gold-500 bg-gold-500/15 text-gold-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
                    {on && <CheckCircle2 className="h-3 w-3" />}{PLAT_LABELS[id] || id}
                  </button>
                );
              })}
              {imgPlatforms.length === 0 && <span className="text-xs text-amber-400">Aucun réseau image connecté</span>}
            </div>
            <Button onClick={publishImage} disabled={imgTest.busy || !imgTest.platforms.length} data-testid="img-test-publish" className="rounded-full bg-emerald-500 text-black hover:bg-emerald-400">
              {imgTest.busy ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Publication…</> : <><Send className="mr-1 h-4 w-4" />Publier l'image</>}
            </Button>
            {imgTest.result?.ok && (
              <div className="mt-2 space-y-1 text-xs" data-testid="img-test-result">
                {(imgTest.result.ids || []).map((p, i) => (
                  <a key={i} href={p.postUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-emerald-400 hover:underline"><CheckCircle2 className="h-3 w-3" />Publié sur {p.platform} →</a>
                ))}
              </div>
            )}
            {imgTest.result && !imgTest.result.ok && <p className="mt-2 text-xs text-amber-400" data-testid="img-test-error">{postErrorText(imgTest.result.error)}</p>}
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
        {/* Create form */}
        <div className="space-y-4 rounded-2xl border border-gold-500/25 bg-card p-5" data-testid="schedule-form">
          <h3 className="font-display font-semibold">Nouvelle série</h3>
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="schedule-name" placeholder="Nom de la série"
            className="w-full rounded-xl border border-border bg-secondary/40 px-4 py-2.5 text-sm outline-none focus:border-gold-500/60" />
          <textarea value={form.idea} onChange={(e) => setForm({ ...form, idea: e.target.value })} data-testid="schedule-idea" rows={4} placeholder="Votre idée : ex. « clip vertical dynamique sur les bienfaits du café le matin, ambiance chaleureuse »"
            className="w-full resize-none rounded-xl border border-border bg-secondary/40 px-4 py-2.5 text-sm outline-none focus:border-gold-500/60" />

          <div>
            <label className="mb-1.5 block font-mono text-xs uppercase tracking-wider text-muted-foreground">Image de référence (optionnelle)</label>
            <div className="flex items-center gap-3">
              <button onClick={() => fileRef.current?.click()} data-testid="schedule-image-btn" className="flex items-center gap-2 rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm text-muted-foreground hover:text-foreground"><ImagePlus className="h-4 w-4" />Choisir</button>
              {form.image && <img src={form.image} alt="cover" className="h-12 w-12 rounded-lg object-cover ring-1 ring-gold-500/30" />}
              <input ref={fileRef} type="file" accept="image/*" onChange={onImage} className="hidden" data-testid="schedule-image-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1.5 block font-mono text-xs uppercase tracking-wider text-muted-foreground">Cadence</label>
              <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })} data-testid="schedule-cadence"
                className="w-full rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-sm outline-none focus:border-gold-500/60">
                {CADENCE.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            {form.cadence === "daily" && (
              <div>
                <label className="mb-1.5 block font-mono text-xs uppercase tracking-wider text-muted-foreground">Heure</label>
                <input type="time" value={form.time_of_day} onChange={(e) => setForm({ ...form, time_of_day: e.target.value })} data-testid="schedule-time"
                  className="w-full rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-sm outline-none focus:border-gold-500/60" />
              </div>
            )}
          </div>

          {unlimited ? (
            <div className="rounded-xl border border-gold-500/30 bg-gold-500/10 px-3 py-2 text-xs text-gold-400">Compte admin : génération <span className="font-semibold">illimitée</span>, sans coût de crédits.</div>
          ) : (
            <div>
              <label className="mb-1.5 block font-mono text-xs uppercase tracking-wider text-muted-foreground">Limite / jour (25 cr. par clip)</label>
              <input type="number" min={1} max={50} value={form.daily_limit} onChange={(e) => setForm({ ...form, daily_limit: parseInt(e.target.value || "1") })} data-testid="schedule-limit"
                className="w-full rounded-xl border border-border bg-secondary/40 px-3 py-2.5 text-sm outline-none focus:border-gold-500/60" />
            </div>
          )}

          <div className="space-y-2 rounded-xl border border-border bg-secondary/20 p-3" data-testid="autopost-block">
            <button onClick={() => setForm({ ...form, autopost: !form.autopost })} data-testid="autopost-toggle" className="flex w-full items-center justify-between text-sm">
              <span className="flex items-center gap-2 font-medium"><Share2 className="h-4 w-4 text-gold-400" />Publier automatiquement</span>
              <span className={`relative h-5 w-9 rounded-full transition-colors ${form.autopost ? "bg-gold-500" : "bg-border"}`}><span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${form.autopost ? "left-4" : "left-0.5"}`} /></span>
            </button>
            {form.autopost && (
              <div className="space-y-2 pt-1">
                <div className="flex flex-wrap gap-2">
                  {availablePlatforms.map((id) => {
                    const on = form.platforms.includes(id);
                    return (
                      <button key={id} onClick={() => togglePlatform(id)} data-testid={`platform-${id}`}
                        className={`flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-gold-500 bg-gold-500/15 text-gold-400" : "border-border text-muted-foreground hover:text-foreground"}`}>
                        {on && <CheckCircle2 className="h-3 w-3" />}{PLAT_LABELS[id] || id}
                      </button>
                    );
                  })}
                </div>
                <input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} data-testid="autopost-caption" placeholder="Légende (optionnelle) — sinon l'idée est utilisée"
                  className="w-full rounded-xl border border-border bg-secondary/40 px-3 py-2 text-sm outline-none focus:border-gold-500/60" />
              </div>
            )}
            <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
              {social.accounts?.length ? (
                <span className="flex items-center gap-1 text-emerald-400"><CheckCircle2 className="h-3 w-3" />Connecté : {social.accounts.join(", ")}</span>
              ) : (
                <span className="flex items-center gap-1 text-amber-400"><AlertTriangle className="h-3 w-3" />Aucun réseau connecté</span>
              )}
              <a href="https://app.ayrshare.com" target="_blank" rel="noreferrer" className="flex items-center gap-1 text-gold-400 hover:underline" data-testid="ayrshare-link"><Link2 className="h-3 w-3" />Gérer les réseaux</a>
            </div>
          </div>

          <Button onClick={create} disabled={creating || !form.idea.trim()} data-testid="schedule-create-btn" className="w-full rounded-full bg-gold-500 text-black hover:bg-gold-400">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" />Programmer la série</>}
          </Button>
        </div>

        {/* Schedules list */}
        <div className="space-y-3">
          <h3 className="font-display font-semibold">Mes séries programmées</h3>
          {schedules.length === 0 && <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Aucune série. Créez-en une pour lancer le pilote automatique.</div>}
          {schedules.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4" data-testid={`schedule-${s.id}`}>
              {s.image ? <img src={s.image} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" /> : <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-gold-500/15 text-gold-400"><Clapperboard className="h-5 w-5" /></div>}
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{s.name}</p>
                <p className="truncate text-xs text-muted-foreground">{s.idea}</p>
                <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">{CADENCE.find((c) => c.id === s.cadence)?.label} · prochain : {nextLabel(s.next_run)}</p>
                {s.autopost && s.platforms?.length ? <p className="mt-0.5 flex items-center gap-1 text-[11px] text-gold-400"><Share2 className="h-3 w-3" />auto-post : {s.platforms.join(", ")}</p> : null}
              </div>
              <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${s.active ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>{s.active ? "actif" : "pause"}</span>
              <button onClick={() => runNow(s.id)} data-testid={`schedule-run-${s.id}`} title="Générer maintenant" className="text-muted-foreground hover:text-gold-400"><Play className="h-4 w-4" /></button>
              <button onClick={() => toggle(s)} data-testid={`schedule-toggle-${s.id}`} title="Activer/pause" className="text-muted-foreground hover:text-foreground"><Power className="h-4 w-4" /></button>
              <button onClick={() => del(s.id)} data-testid={`schedule-del-${s.id}`} title="Supprimer" className="text-muted-foreground hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Library */}
      <div>
        <div className="mb-3 flex items-center gap-2"><Film className="h-4 w-4 text-gold-400" /><h3 className="font-display font-semibold">Bibliothèque</h3><span className="text-xs text-muted-foreground">{videos.length} clip(s)</span></div>
        {videos.length === 0 && <div className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">Les vidéos générées apparaîtront ici automatiquement.</div>}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {videos.map((v) => (
            <motion.div key={v.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="overflow-hidden rounded-2xl border border-border bg-card" data-testid={`video-${v.id}`}>
              <div className="relative aspect-[9/16] bg-[#070B19]">
                {v.status === "done" && v.url ? (
                  <video src={v.url} controls poster={v.cover || undefined} className="h-full w-full object-cover" data-testid={`video-player-${v.id}`} />
                ) : v.status === "failed" ? (
                  <div className="grid h-full place-items-center p-4 text-center text-xs text-red-300"><div><AlertTriangle className="mx-auto mb-1 h-5 w-5" />{v.error || "Échec"}</div></div>
                ) : (
                  <div className="grid h-full place-items-center text-gold-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
                )}
              </div>
              <div className="p-3">
                <p className="truncate text-xs text-muted-foreground" title={v.idea}>{v.idea}</p>
                {v.post && (v.post.ok === true
                  ? <p className="mt-1 flex items-center gap-1 text-[11px] text-emerald-400" data-testid={`video-posted-${v.id}`}><CheckCircle2 className="h-3 w-3" />publié : {(v.post.platforms || []).join(", ")}</p>
                  : v.post.ok === null
                    ? <p className="mt-1 flex items-center gap-1 text-[11px] text-gold-400"><Loader2 className="h-3 w-3 animate-spin" />publication en cours…</p>
                    : <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-400"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{postErrorText(v.post.error)}</p>)}
                <div className="mt-2 flex items-center justify-between">
                  {v.status === "done" && v.url ? (
                    <div className="flex items-center gap-3">
                      <a href={v.url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-xs text-gold-400"><Download className="h-3.5 w-3.5" />Télécharger</a>
                      <button onClick={() => publish(v)} disabled={publishing === v.id} data-testid={`video-publish-${v.id}`} className="flex items-center gap-1 text-xs text-gold-400 hover:underline disabled:opacity-50">
                        {publishing === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}Publier
                      </button>
                    </div>
                  ) : <span className="text-[11px] text-muted-foreground">{v.status}</span>}
                  <button onClick={() => delVideo(v.id)} data-testid={`video-del-${v.id}`} className="text-muted-foreground hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
